import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiUrl } from '../utils/urls';
import { Button, SectionHeading } from './ui';

const inputClassName =
  'w-full rounded-btn border border-white/10 bg-bg px-4 py-3 text-sm text-body placeholder:text-muted/70 transition-colors focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15';

const Newsletter = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [contactStatus, setContactStatus] = useState({ loading: false, message: '', error: false });
  const [newsletterStatus, setNewsletterStatus] = useState({
    loading: false,
    message: '',
    error: false,
  });

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setNewsletterStatus({
        loading: false,
        message: t('home.newsletter.validation.required'),
        error: true,
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setNewsletterStatus({
        loading: false,
        message: t('home.newsletter.validation.error'),
        error: true,
      });
      return;
    }

    setNewsletterStatus({ loading: true, message: '', error: false });

    try {
      const response = await axios.post(getApiUrl('/newsletter/subscribe'), { email });

      if (response.data.success) {
        setNewsletterStatus({
          loading: false,
          message: response.data.message,
          error: false,
        });
        setEmail('');
      } else {
        throw new Error(response.data.message || "Erreur lors de l'abonnement");
      }
    } catch (error) {
      console.error('Erreur abonnement newsletter:', error);
      setNewsletterStatus({
        loading: false,
        message: error.response?.data?.message || t('home.newsletter.error'),
        error: true,
      });
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setContactStatus({
        loading: false,
        message: t('home.contact.validation.required'),
        error: true,
      });
      return;
    }

    setContactStatus({ loading: true, message: '', error: false });

    try {
      const response = await axios.post(getApiUrl('/email/contact'), formData);

      if (response.data.success) {
        setContactStatus({
          loading: false,
          message: response.data.message,
          error: false,
        });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
        });
      } else {
        throw new Error(response.data.message || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error('Erreur envoi formulaire de contact:', error);
      setContactStatus({
        loading: false,
        message: error.response?.data?.message || t('home.contact.validation.error'),
        error: true,
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact-section" className="border-t border-white/[.06] bg-bg py-24 md:py-section">
      <div className="mx-auto max-w-page px-6">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <SectionHeading
            title={t('home.newsletter.title')}
            subtitle={t('home.newsletter.description')}
            className="mb-0 [&>h2]:mx-auto [&>p]:mx-auto"
          />
        </div>

        <form onSubmit={handleNewsletterSubmit} className="mx-auto mb-20 max-w-xl">
          <div className="flex flex-col gap-3 rounded-card border border-white/[.08] bg-surface p-2 sm:flex-row sm:items-center">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('home.newsletter.placeholder')}
              className={`flex-1 !border-0 !bg-transparent focus:!ring-0 ${inputClassName}`}
              required
            />
            <Button type="submit" disabled={newsletterStatus.loading} className="shrink-0">
              {newsletterStatus.loading
                ? t('home.newsletter.loading')
                : t('home.newsletter.subscribe')}
            </Button>
          </div>

          {newsletterStatus.message && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-3 rounded-btn p-3 text-center text-sm ${
                newsletterStatus.error
                  ? 'border border-danger/20 bg-danger/10 text-danger'
                  : 'border border-success/20 bg-success/10 text-success'
              }`}
            >
              {newsletterStatus.message}
            </motion.div>
          )}
        </form>

        <div className="mx-auto max-w-xl">
          <SectionHeading
            title={t('home.contact.title')}
            subtitle={t('home.contact.description')}
            className="mb-8 text-center [&>h2]:mx-auto [&>p]:mx-auto"
          />

          <form
            onSubmit={handleContactSubmit}
            className="rounded-card border border-white/[.08] bg-surface p-6 md:p-8"
          >
            <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <input
                type="text"
                name="name"
                placeholder={t('home.contact.placeholders.name')}
                value={formData.name}
                onChange={handleChange}
                className={inputClassName}
                required
              />
              <input
                type="email"
                name="email"
                placeholder={t('home.contact.placeholders.email')}
                value={formData.email}
                onChange={handleChange}
                className={inputClassName}
                required
              />
            </div>

            <div className="mb-3">
              <input
                type="text"
                name="subject"
                placeholder={t('home.contact.placeholders.subject')}
                value={formData.subject}
                onChange={handleChange}
                className={inputClassName}
                required
              />
            </div>

            <div className="mb-4">
              <textarea
                name="message"
                placeholder={t('home.contact.placeholders.message')}
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className={inputClassName}
                required
              />
            </div>

            <Button type="submit" disabled={contactStatus.loading} className="w-full">
              {contactStatus.loading ? t('home.contact.loading') : t('home.contact.sendButton')}
            </Button>

            {contactStatus.message && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-3 rounded-btn p-3 text-center text-sm ${
                  contactStatus.error
                    ? 'border border-danger/20 bg-danger/10 text-danger'
                    : 'border border-success/20 bg-success/10 text-success'
                }`}
              >
                {contactStatus.message}
              </motion.div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;
